import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppPageProps } from './AppPageProps';
import { getUserProfileAndDocPermission } from '@store/actions/UserProfileActionCreators';
import Toast from '../Toast/Toast';
import {
	UserProfileHasARoleSelector,
	UserProfileDetailsSelector,
	UserProfileSelector,
} from '@store/selectors/UserProfileSelector';
import { RootState } from '@interfaces/rootState';
import { LocationDescriptor } from 'history';
import { useAuthBaseContext } from '../AuthBase/AuthBase';
import { StorageKey } from '@utilities/authConstants';
import { UserStatus } from '@interfaces/UserProfile/UserStatusEnum';
import { AuthSelector } from '@store/selectors/AuthSelector';
import { useAppPageControlledProperties } from './AppPageContext/AppPageContext';
import { BackToHomeLink } from './BackToHomeLink/BackToHomeLink';
import classNames from 'classnames';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import { AuthenticatedBanner } from '@components/AuthenticatedBanner/AuthenticatedBanner';
import { Chat } from '@components/Chat/Chat';
import { AppHeader } from '@components/AppPage/AppHeader';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100vh',
		minHeight: 0,
	},
	content: {
		flex: '1 1 auto',
		minHeight: 0,
		overflowY: 'scroll',
		overflowX: 'hidden',
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: theme.palette.common.white,
	},
	contentInner: {
		marginLeft: theme.spacing(5),
		marginRight: theme.spacing(5),
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	componentContainer: {
		paddingBottom: theme.spacing(5),
	},
	alertContainer: {
		position: 'fixed',
		top: 80,
		zIndex: 120,
	},
	alertBox: {
		boxShadow: theme.shadows[2],
	},
}));

function AppPageInner<StateType = void>({
	component: Component,
	hideDropDown,
	hideRequestButton,
	disableAuthentication,
	showHomeLink,
	onlyAllowRoles,
	forbiddenRedirectTo,
	hasAlert,
	subHeaderTitle,
	hasBanner = true,
	...rest
}: AppPageProps<StateType>) {
	const classes = useStyles();

	const { location } = useHistory<Record<string, boolean>>();
	const dispatch = useDispatch();
	const { accessToken } = useSelector(AuthSelector);
	const hasAccessToken = !!accessToken;
	const { navToRef } = useAuthBaseContext();
	const { bodyClass } = useAppPageControlledProperties();

	const allowedRole = useSelector<RootState, boolean | undefined>(
		UserProfileHasARoleSelector(onlyAllowRoles),
	);
	const { termsOfUseAccepted, userId, status } = useSelector(UserProfileDetailsSelector);
	const { error } = useSelector(UserProfileSelector);

	React.useEffect(() => {
		if (window.config.DISABLE_AUTH || hasAccessToken) {
			dispatch(getUserProfileAndDocPermission());
		}
	}, [dispatch, hasAccessToken]);

	const updateNavToRef = React.useCallback(
		(storeInSession = false) => {
			const { pathname, search, hash } = location;
			navToRef.current = { pathname, search, hash };
			if (storeInSession) {
				window.sessionStorage.setItem(
					StorageKey.NAV_TO,
					btoa(JSON.stringify(navToRef.current)),
				);
			}
		},
		[location, navToRef],
	);

	React.useEffect(() => {
		if (!hasAccessToken) {
			return;
		}
		updateNavToRef(true);
	}, [hasAccessToken, updateNavToRef]);

	React.useEffect(() => {
		if (location.state && location.state.shouldReloadApp) {
			window.location.reload();
		}
	}, [location.state]);

	const customNavToHomeText = typeof showHomeLink === 'string' ? showHomeLink : undefined;
	const isForbidden: boolean = !!onlyAllowRoles && allowedRole === false;

	const termsOfUseAcceptedLocal = !location.state?.fromTermsOfUsePage;

	let redirectTo: LocationDescriptor | null = null;
	if (!window.config.DISABLE_AUTH && !disableAuthentication && !hasAccessToken) {
		updateNavToRef();

		redirectTo = {
			pathname: ROUTE_CONSTANTS.LOGIN,
			hash: location.hash,
		};
	} else if (
		userId &&
		!termsOfUseAccepted &&
		termsOfUseAcceptedLocal &&
		location.pathname !== ROUTE_CONSTANTS.TERMS_OF_USE
	) {
		redirectTo = {
			pathname: ROUTE_CONSTANTS.TERMS_OF_USE,
			state: {
				original: {
					pathname: location.pathname,
					search: location.search,
					hash: location.hash,
				},
			},
		};
	} else if (
		userId &&
		termsOfUseAccepted &&
		status === UserStatus.PENDING_ACTIVE &&
		location.pathname !== ROUTE_CONSTANTS.SETUP_PROFILE
	) {
		redirectTo = {
			pathname: ROUTE_CONSTANTS.SETUP_PROFILE,
		};
	} else if (status === UserStatus.LOCK && location.pathname !== ROUTE_CONSTANTS.SETUP_PROFILE) {
		redirectTo = {
			pathname: ROUTE_CONSTANTS.SETUP_PROFILE,
		};
	} else if (isForbidden) {
		redirectTo = forbiddenRedirectTo || ROUTE_CONSTANTS.HOME;
	}

	if (redirectTo) {
		return <Redirect to={redirectTo} />;
	}

	if (!disableAuthentication && !window.config.DISABLE_AUTH && userId == null && error === null) {
		return null;
	}

	const baseLocationKey = location.pathname.split('/')[1];

	return (
		<Route
			{...rest}
			render={() => (
				<Box className={classes.root}>
					<AppHeader
						hideRequestButton={hideRequestButton}
						hideDropDown={hideDropDown}
						subHeaderTitle={subHeaderTitle}
					/>

					{hasAlert && (
						<Box display="flex" justifyContent="center">
							<Box className={classes.alertContainer}>
								<Box className={classes.alertBox}>
									<Toast />
								</Box>
							</Box>
						</Box>
					)}

					<Chat />

					<Box
						key={baseLocationKey}
						className={classNames(classes.content, bodyClass)}
					>
						<Box className={classes.contentInner}>
							{showHomeLink && <BackToHomeLink navToHomeText={customNavToHomeText} />}
							<Box
								className={classes.componentContainer}
								data-field="component-container"
							>
								{hasBanner && <AuthenticatedBanner />}
								<Component />
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		/>
	);
}

export function AppPage<StateType = void>(props: AppPageProps<StateType>) {
	return <AppPageInner {...props} />;
}
